import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ToolShell, OptionRow, ToolNav } from "@/pages/tools/_shared";

const QUESTIONS = [
  { key: "control_owners", text: "Do all your ISO 27001 controls have named owners?" },
  { key: "controls_tested", text: "Are your controls tested (not just documented) at least annually?" },
  { key: "policies_current", text: "Are your information security policies current and formally approved?" },
  { key: "evidence_ready", text: "Can you produce evidence for any control within one working day?" },
  { key: "risk_register", text: "Do you maintain an active risk register reviewed by leadership quarterly?" },
  { key: "supplier_reviews", text: "Do you assess key suppliers against your security requirements?" },
  { key: "training_program", text: "Do all staff complete security awareness training annually?" },
  { key: "incident_playbook", text: "Do you have a written incident response playbook that is exercised?" },
  { key: "access_reviews", text: "Are user access rights reviewed at least quarterly?" },
  { key: "management_review", text: "Does leadership formally review the ISMS at least twice a year?" },
];
const OPTIONS = [
  { value: "yes", label: "Yes", weight: 1 },
  { value: "partial", label: "Partial", weight: 0.5 },
  { value: "no", label: "No", weight: 0 },
];

function compute(answers) {
  let total = 0;
  QUESTIONS.forEach((q) => {
    const o = OPTIONS.find((x) => x.value === answers[q.key]);
    total += o ? o.weight : 0;
  });
  const score = Math.round((total / QUESTIONS.length) * 100);
  let band = "Not ready";
  if (score >= 80) band = "Audit-ready";
  else if (score >= 60) band = "Close to ready";
  else if (score >= 40) band = "In progress";
  const topFix = QUESTIONS.find((q) => answers[q.key] !== "yes")?.text || "Keep control tests current.";
  return { score, band, topFix };
}

export default function ISO27001() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });
  const total = QUESTIONS.length + 1;

  if (state.stage === "result") {
    const { score, band, topFix } = state.result;
    return (
      <ToolShell eyebrow="ISO 27001 Readiness" title="Your readiness score">
        <div className="rounded-2xl border border-wafa-deep/10 bg-wafa-cream/40 p-8 text-center">
          <div className="font-heading text-6xl font-semibold text-wafa-deep">{score}</div>
          <div className="mt-1 text-xs uppercase tracking-widest text-wafa-ink/60">out of 100</div>
          <div className="mt-4 inline-flex rounded-full bg-wafa-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-wafa-deep">{band}</div>
        </div>
        <div className="mt-8 rounded-lg border border-wafa-deep/10 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-wafa-primary">What to fix first</div>
          <div className="mt-2 text-base text-wafa-ink/85">{topFix}</div>
        </div>
        <a href="/contact" data-testid="iso-result-cta" className="mt-10 inline-flex w-full items-center justify-center rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep">
          Talk to Meyar
        </a>
      </ToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        setState({ ...state, error: "Please enter a valid email address." });
        return;
      }
      const r = compute(answers);
      try {
        await api.post("/tools/iso-27001-readiness", {
          answers: Object.entries(answers).map(([k, v]) => ({ key: k, value: v })),
          score: r.score,
          band: r.band,
          top_fix: r.topFix,
          email: contact.email,
          name: contact.name,
          company: contact.company,
        });
        setState({ stage: "result", result: r });
      } catch (err) {
        setState({ ...state, error: "Failed to send. Please try again." });
      }
    };
    return (
      <ToolShell eyebrow="ISO 27001 Readiness" title="See your score" step={total} totalSteps={total}>
        <form onSubmit={submit} className="space-y-4" data-testid="iso-gate-form">
          <input placeholder="Your name" data-testid="iso-name" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          <input required type="email" placeholder="Work email *" data-testid="iso-email" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          <input placeholder="Company" data-testid="iso-company" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          {state.error ? <div className="flex items-center gap-2 text-sm text-wafa-tomato"><AlertCircle className="h-4 w-4" />{state.error}</div> : null}
          <button data-testid="iso-see-score" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep">See my score</button>
        </form>
      </ToolShell>
    );
  }

  const q = QUESTIONS[step];
  const answered = !!answers[q.key];
  return (
    <ToolShell eyebrow="ISO 27001 Readiness" title="10 questions." subtitle="Answer for how things really are today." step={step + 1} totalSteps={total}>
      <div className="font-heading text-xl font-medium text-wafa-deep sm:text-2xl">{q.text}</div>
      <div className="mt-6 space-y-2.5">
        {OPTIONS.map((o) => (
          <OptionRow key={o.value} value={o.value} current={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} label={o.label} testId={`iso-option-${q.key}-${o.value}`} />
        ))}
      </div>
      <ToolNav
        onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => { if (step === QUESTIONS.length - 1) setState({ stage: "gate", error: "" }); else setStep(step + 1); }}
        disableNext={!answered}
        nextLabel={step === QUESTIONS.length - 1 ? "Finish" : "Next"}
      />
    </ToolShell>
  );
}
